/**
 * Shared ZPL printer list + persisted selection for hub pages that send labels to the print relay.
 */

import { useEffect, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import { apiFetch } from "@/lib/api";
import {
  buildScopedPrinterStorageKey,
  readFloorDeviceId,
  readFloorTabletMobility,
  readScopedZplPrinter,
  writeScopedZplPrinter,
} from "@/lib/floorPrinterStorage";

export {
  formatZplPrinterLocationStockDetail,
  formatZplPrinterOptionLabel,
  zplPrinterListIncludesRef,
  type ZplPrinterListItem,
} from "./zplPrinterDisplay";

import type { ZplPrinterListItem } from "./zplPrinterDisplay";
import { zplPrinterListIncludesRef } from "./zplPrinterDisplay";

export const ZPL_DEFAULT_PRINTER_NAME = "Zebra ZT510";

export type ZplPrinterListPayload = {
  printers: ZplPrinterListItem[];
  printerListSource: "print-server" | "default" | "registry";
};

export const ZPL_PRINTERS_QUERY_KEY = ["hub", "zpl-printers"] as const;

export async function fetchZplPrinters(): Promise<ZplPrinterListPayload> {
  return apiFetch<ZplPrinterListPayload>("/api/zpl-printers");
}

/** Browser localStorage keys — one per workflow so pre-pack and potting remember independently. */
export const ZPL_PRINTER_STORAGE_KEYS = {
  prepack: "prepack-zpl-printer",
  potting: "potting-send-zpl-printer",
  assembly: "assembly-zpl-printer",
  adminLabelTemplates: "admin-label-templates-zpl-printer",
  adminZplPrinters: "admin-zpl-printers-label-printer",
  marketingLabelTemplates: "marketing-label-templates-zpl-printer",
  boxLabelGenerate: "box-label-generate-zpl-printer",
  suntronicBoxLabels: "suntronic-box-labels-zpl-printer",
  shippingInbound: "shipping-inbound-zpl-printer",
  clampQc: "clamp-qc-zpl-printer",
  shippingOutbound: "shipping-outbound-zpl-printer",
  kitting: "kitting-zpl-printer",
  personalPddCatch: "personal-pdd-zpl-catch-printer",
  qualityAnalysisLoans: "quality-analysis-loans-zpl-printer",
} as const;

export type ZplPrinterStorageKey =
  (typeof ZPL_PRINTER_STORAGE_KEYS)[keyof typeof ZPL_PRINTER_STORAGE_KEYS];

export type ZplPrintersQuerySnapshot = Pick<
  UseQueryResult<ZplPrinterListPayload>,
  "data" | "isLoading" | "isError" | "error"
>;

export function readStoredZplPrinter(
  storageKey: string,
  fallback = ZPL_DEFAULT_PRINTER_NAME,
  options?: {
    userId?: string;
    deviceId?: string;
    mobility?: "mobile" | "stationary";
    preferLabelSize?: string;
    preferDpi?: number;
    printers?: ZplPrinterListItem[];
  },
): string {
  const userId = options?.userId ?? "";
  const deviceId = options?.deviceId ?? readFloorDeviceId();
  const mobility = options?.mobility ?? readFloorTabletMobility();
  return readScopedZplPrinter(storageKey, {
    userId,
    deviceId,
    mobility,
    fallback,
    preferLabelSize: options?.preferLabelSize,
    preferDpi: options?.preferDpi,
    printers: options?.printers,
  });
}

export function writeStoredZplPrinter(
  storageKey: string,
  printer: string,
  options?: {
    userId?: string;
    deviceId?: string;
  },
): void {
  writeScopedZplPrinter(storageKey, printer, {
    userId: options?.userId ?? "",
    deviceId: options?.deviceId ?? readFloorDeviceId(),
  });
}

export type UseZplPrinterOptions = {
  storageKey: ZplPrinterStorageKey | string;
  /** Used when nothing is stored yet (pre-pack defaults to Zebra ZT510; potting waits for the relay list). */
  defaultPrinter?: string;
  /** Stationary tablets can default to the nearest matching label stock. */
  preferLabelSize?: string;
  preferDpi?: number;
  /** When false, skips the relay printer list until enabled (stored selection still works). */
  fetchPrintersEnabled?: boolean;
};

export function useZplPrinter(options: UseZplPrinterOptions): {
  printersQuery: UseQueryResult<ZplPrinterListPayload>;
  selectedPrinter: string;
  setSelectedPrinter: (printer: string) => void;
} {
  const defaultPrinter = options.defaultPrinter ?? ZPL_DEFAULT_PRINTER_NAME;
  const { session } = useAuth();
  const userId = session?.user?.id ?? "";
  const deviceId = readFloorDeviceId();
  const mobility = readFloorTabletMobility();

  const printersQuery = useQuery({
    queryKey: ZPL_PRINTERS_QUERY_KEY,
    queryFn: fetchZplPrinters,
    staleTime: 120_000,
    enabled: options.fetchPrintersEnabled !== false,
  });

  const [selectedPrinter, setSelectedPrinter] = useState<string>(defaultPrinter);

  useEffect(() => {
    const stored = readStoredZplPrinter(options.storageKey, defaultPrinter, {
      userId,
      deviceId,
      mobility,
      preferLabelSize: options.preferLabelSize,
      preferDpi: options.preferDpi,
      printers: printersQuery.data?.printers,
    });
    setSelectedPrinter(stored);
  }, [
    defaultPrinter,
    deviceId,
    mobility,
    options.preferDpi,
    options.preferLabelSize,
    options.storageKey,
    printersQuery.data?.printers,
    userId,
  ]);

  useEffect(() => {
    writeStoredZplPrinter(options.storageKey, selectedPrinter, {
      userId,
      deviceId,
    });
  }, [deviceId, options.storageKey, selectedPrinter, userId]);

  useEffect(() => {
    const list = printersQuery.data?.printers;
    if (list == null || list.length === 0) return;
    // Stage 1: queue renames (identifier) leave stale localStorage values. Fall back to
    // the default quietly — no error toast. Stage 2 will replace this select with
    // location-only routing.
    if (!zplPrinterListIncludesRef(list, selectedPrinter)) {
      const fallback = zplPrinterListIncludesRef(list, defaultPrinter)
        ? defaultPrinter
        : list[0]!.name;
      setSelectedPrinter(fallback);
    }
  }, [defaultPrinter, printersQuery.data, selectedPrinter]);

  return { printersQuery, selectedPrinter, setSelectedPrinter };
}

export function scopedPrinterStorageKeyForTests(
  workflowKey: string,
  userId: string,
  deviceId: string,
): string {
  return buildScopedPrinterStorageKey(workflowKey, { userId, deviceId });
}
